import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Phone, Mail, ArrowRight, Loader2, Image as ImageIcon, ChevronRight } from 'lucide-react'
import Lightbox from '../components/Lightbox'

export default function ServicePage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const pageSlug = slug || 'om-os' // Default to om-os if no slug

  const [pageData, setPageData] = useState(null)
  const [galleryData, setGalleryData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lightboxIndex, setLightboxIndex] = useState(-1)

  useEffect(() => {
    setLoading(true)
    setPageData(null)
    setGalleryData(null)

    // Fetch pages from CMS
    fetch('https://api.dev.auxo.dk/public/content/pages')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch pages')
        return res.json()
      })
      .then((data) => {
        if (Array.isArray(data)) {
          const page = data.find((p) => p.page_slug === pageSlug)
          if (page) {
            setPageData(page)
            
            // Set SEO Meta
            document.title = `${page.meta_title || page.title} | Craft Café`
            const metaDesc = document.querySelector('meta[name="description"]')
            if (metaDesc && page.meta_description) {
              metaDesc.setAttribute('content', page.meta_description)
            }

            // Fetch associated gallery if exists
            if (page.gallery_slug) {
              fetch('https://api.dev.auxo.dk/public/content/gallery')
                .then((res) => res.json())
                .then((galleries) => {
                  if (Array.isArray(galleries)) {
                    const gallery = galleries.find((g) => g.gallery_slug === page.gallery_slug)
                    if (gallery) {
                      setGalleryData(gallery)
                    }
                  }
                })
                .catch((err) => console.error('Error fetching gallery:', err))
            }
          } else {
            // Page not found, redirect to home
            navigate('/')
          }
        }
        setLoading(false)
      })
      .catch((err) => {
        console.error('Error fetching page:', err)
        setLoading(false)
      })
  }, [pageSlug, navigate])

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-[100px]">
        <div className="text-center space-y-3">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#e6d5c3]" />
          <p className="text-xs text-stone-400 font-light uppercase tracking-widest">Henter indhold...</p>
        </div>
      </div>
    )
  }

  if (!pageData) return null

  const heroImage = pageData.hero_image && pageData.hero_image.length > 0
    ? pageData.hero_image[0]
    : 'https://assets.auxodev.dk/assets/35c1b004-16b7-404c-98d3-c630dfe6edb4/d0137351-6fb2-49e8-b9ff-9d5f7ea8fbce.jpg'

  return (
    <div className="pt-[70px] md:pt-[100px]">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#faf9f6] text-[#1c2b21] border-b border-stone-200">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt={pageData.title}
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#faf9f6] via-[#faf9f6]/40 to-[#faf9f6]" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-6 py-20 md:py-28 text-center space-y-6">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#b89a6c] font-light">
            Craft Café
          </span>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-wide leading-relaxed text-[#1c2b21]">
            {pageData.title}
          </h1>
          {pageData.subtitle && (
            <p className="text-xs md:text-sm text-stone-600 max-w-2xl mx-auto leading-relaxed font-light uppercase tracking-wider">
              {pageData.subtitle}
            </p>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 max-w-4xl mx-auto px-6">
        {/* Rich Text Content */}
        <div
          className="rich-text-content"
          dangerouslySetInnerHTML={{ __html: pageData.content }}
        />

        {/* Connected Gallery Section */}
        {galleryData && galleryData.images && galleryData.images.length > 0 && (
          <div className="mt-16 pt-16 border-t border-stone-200 space-y-8">
            <div className="flex items-center gap-3">
              <div className="border border-[#b89a6c]/20 p-1.5 text-[#b89a6c]">
                <ImageIcon size={14} />
              </div>
              <h2 className="font-serif text-xl md:text-2xl font-light text-[#1c2b21] tracking-wide">
                Billeder af vores {pageData.title.toLowerCase().includes('tapas') ? 'tapas' : pageData.title.toLowerCase().includes('dansk') ? 'danske mad' : pageData.title.toLowerCase().includes('catering') ? 'catering' : 'anretninger'}
              </h2>
            </div>
            {galleryData.description && (
              <p className="text-stone-600 text-xs font-light -mt-4 leading-relaxed">{galleryData.description}</p>
            )}
            
            <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
              {galleryData.images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative aspect-square overflow-hidden border border-stone-200 bg-white cursor-pointer rounded-none shadow-none"
                >
                  <img
                    src={img}
                    alt={`${galleryData.title} - Billede ${idx + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#faf9f6]/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="border border-[#b89a6c]/30 bg-white/95 p-2 text-[#b89a6c] transform scale-90 group-hover:scale-100 transition-transform rounded-none">
                      <ImageIcon size={14} />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Card */}
        <div className="mt-16 border border-stone-200 bg-[#f4f1ea] p-8 md:p-10 text-center space-y-6 rounded-none">
          <h3 className="font-serif text-xl md:text-2xl font-light text-[#1c2b21] tracking-wide">Klar til at bestille eller har du spørgsmål?</h3>
          <p className="text-xs text-stone-600 max-w-lg mx-auto font-light leading-relaxed">
            Vi sidder klar til at hjælpe dig med at sammensætte den helt rigtige menu til dit arrangement. Ring direkte eller send en forespørgsel.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Link
              to="/kontakt"
              className="bg-[#b89a6c] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-[#1c2b21] transition-colors cursor-pointer rounded-none"
            >
              Send forespørgsel
            </Link>
            <a
              href="tel:+45489981"
              className="border border-stone-300 text-stone-700 px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:border-[#b89a6c] hover:text-[#b89a6c] transition-colors cursor-pointer whitespace-nowrap rounded-none"
            >
              Ring direkte: 45 48 99 81
            </a>
          </div>
        </div>
      </section>

      {/* Lightbox for Gallery */}
      {lightboxIndex >= 0 && galleryData && (
        <Lightbox
          images={galleryData.images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(-1)}
          onNavigate={(newIndex) => setLightboxIndex(newIndex)}
        />
      )}
    </div>
  )
}
